/**
 * @file TreeSelect 树选择
 * @description 下拉中选树节点（单选）；层级带引导线；选中行高亮回显；Portal + fade + 视口边界。
 *
 * @whenToUse 表单选择分类 / 上级部门。
 * @whenNotToUse 页面内常驻树（用 Tree）。
 */

import { useEffect, useId, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { ChevronsUpDown } from 'lucide-react'
import { cn } from '@/lib/cn.ts'
import {
  computeDropdownBox,
  floatingPanelStyle,
  type FloatingBox,
} from '@/lib/floating.ts'
import { ScrollArea } from '@/components/ui/scroll-area.tsx'
import { Tree, type TreeNode } from '@/components/ui/tree.tsx'
import {
  searchControlWidth,
  useSearchForm,
} from '@/components/ui/search-form-context.tsx'

export type TreeSelectProps = {
  treeData: TreeNode[]
  value?: string
  onChange?: (value: string) => void
  placeholder?: string
  disabled?: boolean
  invalid?: boolean
  /** 仅叶子可选；默认 false，任意节点都可选 */
  selectableLeafOnly?: boolean
  /** 树数据尚未匹配到节点时，触发器回显文案 */
  displayLabel?: ReactNode
  className?: string
  id?: string
}

function findTitle(nodes: TreeNode[], key: string): ReactNode | null {
  for (const node of nodes) {
    if (node.key === key) return node.title
    if (node.children) {
      const found = findTitle(node.children, key)
      if (found !== null) return found
    }
  }
  return null
}

/**
 * 收集树中全部 key，供下拉默认展开。
 * @param nodes 树数据
 */
function collectKeys(nodes: TreeNode[]): string[] {
  return nodes.flatMap((node) => [
    node.key,
    ...(node.children ? collectKeys(node.children) : []),
  ])
}

export function TreeSelect({
  treeData,
  value,
  onChange,
  placeholder = '请选择',
  disabled = false,
  invalid = false,
  selectableLeafOnly = false,
  displayLabel,
  className,
  id,
}: TreeSelectProps) {
  const inSearchForm = useSearchForm()
  const panelId = useId()
  const triggerRef = useRef<HTMLButtonElement>(null)
  const panelRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [mounted, setMounted] = useState(false)
  const [visible, setVisible] = useState(false)
  const [box, setBox] = useState<FloatingBox | null>(null)

  const fromTree = value ? findTitle(treeData, value) : null
  const label = fromTree ?? (value ? displayLabel : null)

  const updateBox = () => {
    const el = triggerRef.current
    if (!el) return
    setBox(computeDropdownBox(el.getBoundingClientRect(), 4, 260))
  }

  useEffect(() => {
    if (open) {
      setMounted(true)
      updateBox()
      const frame = requestAnimationFrame(() => setVisible(true))
      return () => cancelAnimationFrame(frame)
    }
    setVisible(false)
    const timer = window.setTimeout(() => setMounted(false), 200)
    return () => window.clearTimeout(timer)
  }, [open])

  useEffect(() => {
    if (!open) return
    const onWin = () => updateBox()
    window.addEventListener('resize', onWin)
    window.addEventListener('scroll', onWin, true)
    return () => {
      window.removeEventListener('resize', onWin)
      window.removeEventListener('scroll', onWin, true)
    }
  }, [open])

  useEffect(() => {
    if (!open) return
    const onPointer = (event: MouseEvent) => {
      const t = event.target as Node
      if (triggerRef.current?.contains(t)) return
      if (panelRef.current?.contains(t)) return
      setOpen(false)
    }
    const onKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', onPointer)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onPointer)
      document.removeEventListener('keydown', onKey)
    }
  }, [open])

  return (
    <>
      <button
        id={id}
        ref={triggerRef}
        type="button"
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={panelId}
        className={cn(
          'inline-flex h-8 items-center justify-between gap-1.5 rounded-lg border bg-raised px-2 text-left text-sm transition-opacity duration-200 disabled:cursor-not-allowed disabled:opacity-50',
          searchControlWidth(inSearchForm),
          invalid ? 'border-danger' : 'border-border',
          className,
        )}
        onClick={() => setOpen((v) => !v)}
      >
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            label ? 'text-foreground' : 'text-muted',
          )}
        >
          {label ?? placeholder}
        </span>
        <ChevronsUpDown className="size-4 shrink-0 text-muted" aria-hidden />
      </button>

      {mounted && box && typeof document !== 'undefined'
        ? createPortal(
            <div
              ref={panelRef}
              id={panelId}
              className={cn(
                'fixed z-50 overflow-hidden rounded-lg border border-border bg-raised transition-opacity duration-200',
                visible ? 'opacity-100' : 'opacity-0',
              )}
              style={floatingPanelStyle(box)}
            >
              <ScrollArea
                className="w-full"
                style={{ height: Math.min(box.maxHeight, 280) }}
              >
                <div className="p-1">
                  <Tree
                    treeData={treeData}
                    defaultExpandedKeys={collectKeys(treeData)}
                    selectedKeys={value ? [value] : []}
                    selectableLeafOnly={selectableLeafOnly}
                    onSelect={(key) => {
                      onChange?.(key)
                      setOpen(false)
                    }}
                  />
                </div>
              </ScrollArea>
            </div>,
            document.body,
          )
        : null}
    </>
  )
}
